import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { IFieldMeta, MetaDataService, ToManyMeta } from '../meta-data.service';
import { IOptionItem } from './autocomplete/autocomplete.component';
import { IChipItem } from './chip-list-autocomplete/chip-list-autocomplete.component';
import {
  CountryOptionsProvider,
  DynamicCertificationsProvider,
  DynamicItemsProvider,
  DynamicOptionsProvider,
  IProvider
} from './option-items-providers';

type IItemsProviderReturnValue = [IProvider<IChipItem>, IChipItem[]];

@Injectable({
  providedIn: 'root'
})
export class ProviderFactory {

  public constructor(private metaDataService: MetaDataService) {
  }

  public getCountryProvider(value: any): Observable<IProvider<IOptionItem>> {

    const getValue: Observable<ToManyMeta[]> = value ?
      this.metaDataService.lookUpEntities('Country', [value])
      : of([]);

    return getValue.pipe(map(results => {
      return new CountryOptionsProvider(this.metaDataService, results.map(m => ({name: m.id, label: m.name})));
    }));
  }

  public getOptionsProvider(value: any, meta: IFieldMeta): Observable<IProvider<IOptionItem>> {

    let getValue: Observable<ToManyMeta[]> = of([]);

    if (value) {
      let entityName;
      if (meta.metaType === 'TO_ONE')
        entityName = meta.toOneType;
      else if (meta.metaType === 'TO_MANY')
        entityName = meta.toManyType;
      else if (meta.metaType === 'COUNTRY')
        entityName = 'Country';
      getValue = this.metaDataService.lookUpEntities(entityName, [value]);
    }

    return getValue.pipe(map(results => {
      return new DynamicOptionsProvider(this.metaDataService, meta, results.map(m => ({name: m.id, label: m.name})));
    }));
  }

  public getItemsProvider(value: number[], meta: IFieldMeta): Observable<IItemsProviderReturnValue> {

    let getValue: Observable<ToManyMeta[]> = of([]);

    if (value && value.length !== 0) {
      let entityName;
      if (meta.metaType === 'TO_ONE')
        entityName = meta.toOneType;
      else if (meta.metaType === 'TO_MANY')
        entityName = meta.toManyType;
      else if (meta.metaType === 'COUNTRY')
        entityName = 'Country';
      getValue = this.metaDataService.lookUpEntities(entityName, value);
    }

    return getValue.pipe(map(results => {
      const selItems: IChipItem[] = [];
      if (value.length > 0) {
        value.forEach(id => {
          const data = results.find(d => d.id === id);
          selItems.push({id: data.id, name: data.name});
        });
      }

      const provider = new DynamicItemsProvider(this.metaDataService, meta, selItems);
      return [provider, selItems];
    }));
  }

  public getCertificationsProvider(value?: number): Observable<IProvider<IOptionItem>> {
    if (value) {
      return this.metaDataService.lookUpEntities('Certification', [value]).pipe(map(results => {
        return new DynamicCertificationsProvider(this.metaDataService, results.map(m => ({name: m.id, label: m.name})), 'Certification');
      }));
    } else {
      return this.metaDataService.lookUp('Certification', '').pipe(map(results => {
        return new DynamicCertificationsProvider(this.metaDataService, results.map(m => ({name: m.id, label: m.name})), 'Certification');
      }));
    }
  }

  public getCertificationGroupsProvider(value?: number): Observable<IProvider<IOptionItem>> {
    if (value) {
      return this.metaDataService.lookUpEntities('CertificationGroup', [value]).pipe(map(results => {
        return new DynamicCertificationsProvider(this.metaDataService, results.map(m => ({name: m.id, label: m.name})), 'CertificationGroup');
      }));
    } else {
      return this.metaDataService.lookUp('CertificationGroup', '').pipe(map(results => {
        return new DynamicCertificationsProvider(this.metaDataService, results.map(m => ({name: m.id, label: m.name})), 'CertificationGroup');
      }));
    }
  }
}
